import './UploadFile.scss';
import React, { FunctionComponent, memo, useCallback, useState } from 'react';
import { Button, notify, Spinner } from 'plume-ui';
import { Accept, useDropzone } from 'react-dropzone';
import cx from 'classnames';
import { useRecoilValue } from 'recoil';
import FormattedMessage from '../../utils/components/FormattedMessage';
import { DependencyContainer } from '../../DependencyContainer';
import { activePartnerAtom } from '../../state';
import { useTranslation } from 'react-i18next';
import { CsvFileSummary, Maybe } from '../../types';

export type UploadFileProps = {
  allowedImageTypes?: Accept;
  callback?: (dat: Maybe<CsvFileSummary>) => void;
};

export type BulkUploadApiResponse = {
  rows: Record<string, string>[];
};

const { csvLocationsFileService } = new DependencyContainer();

const UploadFile: FunctionComponent<UploadFileProps> = (props) => {
  const { t } = useTranslation();
  const activePartner = useRecoilValue(activePartnerAtom);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const allowedImagesTypes = props.allowedImageTypes || {
    'text/plain': ['.csv'],
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!activePartner) {
        return;
      }
      const formData = new FormData();
      const [file] = acceptedFiles;
      formData.append('file', file);
      setIsUploading(true);
      try {
        if (activePartner?.id) {
          const response = await csvLocationsFileService.createCsvLocationsFile(
            activePartner.id,
            formData,
          );
          notify({
            type: 'success',
            title: t('success'),
            body: t('uploadFile.csvUploaded'),
          });
          if (props.callback && response) {
            const csvfileSummary: CsvFileSummary = {
              file_id: response.fileId,
              name: response.name,
              updatedAt: response.createdAt,
              size: response.size,
            };
            props.callback(csvfileSummary);
          }
        }
      } catch (e) {
        setUploadFailed(true);
        notify({
          type: 'error',
          title: t('error'),
          body: t('uploadFile.csvUploadError'),
        });
      } finally {
        setIsUploading(false);
      }
    },
    [activePartner, setUploadFailed],
  );

  const dropzone = useDropzone({
    accept: allowedImagesTypes,
    minSize: 1,
    multiple: false,
    preventDropOnDocument: true,
    noClick: false,
    onDrop,
  });

  const onDropAreaClick = () => {
    dropzone.open();
  };

  const dropAreaClassNames = cx('UploadFile__drop-area dropzone', {
    'UploadFile__drop-area--accepted': dropzone.isDragAccept,
    'UploadFile__drop-area--active': dropzone.isDragActive,
  });

  return (
    <div className="wrapper">
      <div
        {...dropzone.getRootProps()}
        className={dropAreaClassNames}
        onClick={onDropAreaClick}
      >
        {isUploading ? (
          <div className="UploadFile__spinner">
            <Spinner visible />
            <div className="UploadFile__spinner__text">
              <FormattedMessage id="uploadFile.uploading" />
            </div>
          </div>
        ) : uploadFailed ? (
          <>
            <img
              src={'/images/png/UploadFile/cloud-sad.png'}
              alt="upload error"
              width="128"
            />
            <div className="UploadFile__drop-area__container">
              <p>
                <FormattedMessage id="uploadFile.uploadFailed" />
              </p>
              <Button styleVariant="tertiary">
                <FormattedMessage id="uploadFile.tryAgain" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <img
              src={'/images/png/UploadFile/cloud-upload.png'}
              alt="upload"
              width="128"
            />
            <div className="UploadFile__drop-area__container">
              <p>
                <FormattedMessage id="uploadFile.dragAndDrop" />
              </p>
              <Button styleVariant="tertiary">
                <FormattedMessage id="uploadFile.browse" />
              </Button>
            </div>
          </>
        )}
        <input {...dropzone.getInputProps()} disabled={isUploading} />
      </div>
      <div className="UploadFile__hint__wrapper">
        {uploadFailed && (
          <span className="UploadFile__hint--failed">
            <FormattedMessage id="uploadFile.hintFailed"></FormattedMessage>
          </span>
        )}
      </div>
    </div>
  );
};

export default memo(UploadFile);
